import { useMemo, useCallback } from "react";
import { ReferralTable } from "@/core/components/referral-table";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { REFERRAL_FIELD_TO_SORTER } from "@/referrals/constants";
import { REFERRAL_FIELD } from "@/referrals/constants";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";
import { ReferralStats } from "@/referrals/referral-stats";

interface ReferralTableViewProps {
  referrals: IReferral[];
  isLoadingReferrals: boolean;
  selectedReferral: IReferral | null;
  onUpdateReferral: (
    values: Partial<ReferralUpdateParams>,
    referral: IReferral,
  ) => void;
  onDeleteReferral: (referral: IReferral) => void;
  onRowClick: (referral: IReferral) => void;
  onPayout: (referral: IReferral | null) => void;
}

const ReferralTableView = ({
  referrals,
  isLoadingReferrals,
  selectedReferral,
  onUpdateReferral,
  onDeleteReferral,
  onRowClick,
  onPayout,
}: ReferralTableViewProps) => {
  const sortedReferrals = useMemo(
    () =>
      [...referrals].sort((a, b) =>
        REFERRAL_FIELD_TO_SORTER[REFERRAL_FIELD.created_at](b, a),
      ),
    [referrals],
  );

  const handleUpdateReferralById = useCallback(
    (referralId: string, values: Partial<ReferralUpdateParams>) => {
      const referral = referrals.find((r) => r.id === referralId);
      if (referral) {
        onUpdateReferral(values, referral);
      }
    },
    [referrals, onUpdateReferral],
  );

  const handleDeleteReferralById = useCallback(
    (referralId: string) => {
      const referral = referrals.find((r) => r.id === referralId);
      if (referral) {
        onDeleteReferral(referral);
      }
    },
    [referrals, onDeleteReferral],
  );

  return (
    <Stack
      gap={4}
      align="stretch"
      className="w-full xl:min-h-0 xl:flex-1 xl:flex-row"
    >
      <Card className="flex h-page min-h-0 min-w-0 flex-col gap-0 overflow-hidden py-0 pr-1 pl-6 xl:h-auto xl:flex-1">
        <ReferralTable
          referrals={sortedReferrals}
          isLoading={isLoadingReferrals}
          selectedReferral={selectedReferral}
          onRowClick={onRowClick}
          onLost={(referralId: string) =>
            handleUpdateReferralById(referralId, { status: "LOST" })
          }
          onMoveToPaid={(referralId: string) =>
            handleUpdateReferralById(referralId, { status: "PAID" })
          }
          onDelete={(referralId: string) =>
            handleDeleteReferralById(referralId)
          }
          setPayoutReferral={onPayout}
          className="h-full min-h-0"
          showStatusColumn={false}
        />
      </Card>
      {!selectedReferral && <ReferralStats />}
    </Stack>
  );
};

export default ReferralTableView;
